// Estatísticas de relatório por vendedor: check-ins, check-outs, clientes únicos,
// KM rodado (estimado em linha reta entre check-ins do mesmo dia) e tempo de permanência.
import { haversine } from './route';
import { CheckIn } from '../types';

export interface VisitDetail {
  clientName: string;
  checkinAt: Date;
  checkoutAt: Date | null;
  permanenciaMs: number | null;
  resultado?: string | null;
  pedidoStatus?: 'sim' | 'nao' | 'orcamento' | null;
  pedidoFeito?: boolean;
  pedidoValor?: number | null;
}

export interface SellerStat {
  sellerId: string;
  name: string;
  total: number;
  checkouts: number;
  clients: number;
  withNotes: number;
  kmMeters: number;
  avgPermanenciaMs: number | null;
  totalPermanenciaMs: number;
  pedidos: number;      // visitas com pedido confirmado
  pedidoValor: number;  // soma dos valores de pedido (R$)
  orcamentos: number;   // visitas com orçamento
  orcamentoValor: number; // soma dos valores de orçamento (R$)
  visits: VisitDetail[];
}

// Salto máximo aceito entre dois check-ins do mesmo dia (acima disso = GPS errado, ignora).
const MAX_LEG_METERS = 150000; // 150 km

export function computeSellerStats(checkins: CheckIn[]): SellerStat[] {
  const bySeller = new Map<string, any>();

  for (const ci of checkins) {
    if (!ci.sellerId) continue;
    let s = bySeller.get(ci.sellerId);
    if (!s) {
      s = { sellerId: ci.sellerId, name: ci.sellerName, total: 0, checkouts: 0, clientsSet: new Set<string>(), withNotes: 0, totalPermanenciaMs: 0, permanenciaCount: 0, pedidos: 0, pedidoValor: 0, orcamentos: 0, orcamentoValor: 0, visits: [] as VisitDetail[], _all: [] as CheckIn[] };
      bySeller.set(ci.sellerId, s);
    }
    s.total++;
    if (ci.checkoutAt) s.checkouts++;
    s.clientsSet.add(ci.clientId);
    if (ci.notes) s.withNotes++;
    // status comercial: usa pedidoStatus; cai no pedidoFeito (dados antigos)
    const pStatus = ci.pedidoStatus || (ci.pedidoFeito ? 'sim' : 'nao');
    if (pStatus === 'sim') { s.pedidos++; s.pedidoValor += Number(ci.pedidoValor || 0); }
    else if (pStatus === 'orcamento') { s.orcamentos++; s.orcamentoValor += Number(ci.pedidoValor || 0); }
    const permanenciaMs = ci.checkoutAt ? ci.checkoutAt.getTime() - ci.createdAt.getTime() : null;
    if (permanenciaMs != null && permanenciaMs >= 0) { s.totalPermanenciaMs += permanenciaMs; s.permanenciaCount++; }
    s.visits.push({ clientName: ci.clientName, checkinAt: ci.createdAt, checkoutAt: ci.checkoutAt ?? null, permanenciaMs, resultado: ci.resultado ?? null, pedidoStatus: pStatus, pedidoFeito: pStatus === 'sim', pedidoValor: ci.pedidoValor ?? null });
    s._all.push(ci);
  }

  const out: SellerStat[] = [];
  Array.from(bySeller.values()).forEach((s: any) => {
    // KM rodado: soma das distâncias entre check-ins consecutivos do mesmo dia
    const byDay = new Map<string, CheckIn[]>();
    (s._all as CheckIn[]).forEach((ci) => {
      if (!ci.location?.latitude || !ci.location?.longitude) return;
      const key = ci.createdAt.toDateString();
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(ci);
    });
    let km = 0;
    Array.from(byDay.values()).forEach((arr) => {
      arr.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
      for (let i = 1; i < arr.length; i++) {
        const leg = haversine(
          { lat: arr[i - 1].location.latitude, lng: arr[i - 1].location.longitude },
          { lat: arr[i].location.latitude, lng: arr[i].location.longitude }
        );
        // Ignora "saltos" absurdos entre dois check-ins do mesmo dia (GPS impreciso/disparado):
        // um único ponto errado infla o total em centenas de km.
        if (leg <= MAX_LEG_METERS) km += leg;
      }
    });
    s.visits.sort((a: VisitDetail, b: VisitDetail) => b.checkinAt.getTime() - a.checkinAt.getTime());
    out.push({
      sellerId: s.sellerId,
      name: s.name,
      total: s.total,
      checkouts: s.checkouts,
      clients: s.clientsSet.size,
      withNotes: s.withNotes,
      kmMeters: km,
      avgPermanenciaMs: s.permanenciaCount ? s.totalPermanenciaMs / s.permanenciaCount : null,
      totalPermanenciaMs: s.totalPermanenciaMs,
      pedidos: s.pedidos,
      pedidoValor: s.pedidoValor,
      orcamentos: s.orcamentos,
      orcamentoValor: s.orcamentoValor,
      visits: s.visits,
    });
  });
  out.sort((a, b) => b.total - a.total);
  return out;
}

export const fmtKmStat = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

export const fmtBRL = (v: number) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDuration = (ms: number | null): string => {
  if (ms == null) return '—';
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r ? `${h}h ${r}min` : `${h}h`;
};
